import { IS_PROD } from '@/config';
import { useCallback, useRef, useState } from 'react';

/**
 * IMPORTANT:
 *
 * If you using Google Chrome, you need to enable the following flag for clientarea URL:
 * chrome://flags/#unsafely-treat-insecure-origin-as-secure
 *
 * This flag is used to allow the use of the microphone in insecure origins.
 */

const useAudioRecorder = ({ onStop }) => {
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [recording, setRecording] = useState(false);

  const resetRecording = (recorder) => {
    if (!recorder || !recorder.stream) return;
    recorder.stream.getTracks().forEach(track => track.stop());
    recorderRef.current = null;
    chunksRef.current = [];
  };

  const handleStartRecording = useCallback(() => {
    chunksRef.current = [];
    setRecording(true);

    try {
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then((stream) => {
          const recorder = new MediaRecorder(stream);

          recorder.ondataavailable = (e) => {
            if (e.data.size > 0) {
              chunksRef.current.push(e.data);
            }
          };

          recorderRef.current = recorder;
          recorder.start();
        });
    } catch (error) {
      if (!IS_PROD) {
        console.error('Erro ao iniciar gravação:', error);
      }
      setRecording(false);
    }
  }, []);

  const handleStopRecording = useCallback(({ cancelRecording }) => {
    const recorder = recorderRef.current;
    if (!recorder) return;

    setRecording(false);

    recorder.onstop = () => {
      if (!cancelRecording && chunksRef.current.length > 0) {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/wav' });
        onStop(audioBlob);
      }
      resetRecording(recorder);
    };

    recorder.stop();
  }, [onStop]);

  return {
    recording,
    handleStartRecording,
    handleStopRecording,
  };
};

export default useAudioRecorder;
